/**
 * Support-ticket label -> Jira issue-type id. Typed here rather than at each consumer: as `any` it
 * made every `jiraIdMapper["…"]` read an unchecked `any`, which eight call sites across
 * packages/server and packages/web each worked around with their own re-typing alias.
 */
export const jiraIdMapper: Record<
  "Question" | "Feature Request" | "Report a Bug",
  string
> = {
  Question: "10010",
  "Feature Request": "10012",
  "Report a Bug": "10013",
};

export const issueTypeIdToLabel = (t?: any): Record<string, string> => {
  if (!t) t = (s: string) => s;
  return {
    "10012": t("Feature Request"),
    "10013": t("Report a Bug"),
    "10010": t("Question"),
  };
};

export const impactIdToLabel = (t?: any): Record<string, string> => {
  if (!t) t = (s: string) => s;
  return {
    "10000": t("Extensive"),
    "10001": t("Significant"),
    "10002": t("Moderate"),
    "10003": t("Minor"),
  };
};

export const JIRA_CREATED_BY_FILTERS = {
  CREATED_BY_ME: "createdByMe",
  CREATED_BY_ORG_USERS: "createdByOrgUsers",
  CREATED_BY_PORTAL_USERS: "createdByPortalUsers",
  CREATED_BY_ANY_PORTAL_USERS: "createdByAnyPortalUsers",
} as const;

export type JiraCreatedByFilter =
  (typeof JIRA_CREATED_BY_FILTERS)[keyof typeof JIRA_CREATED_BY_FILTERS];

export const supportTicketTypes: any = {
  SUGGEST_A_FEATURE: "SUGGEST_A_FEATURE",
  REPORT_A_BUG: "REPORT_A_BUG",
  ASK_QUESTION: "ASK_QUESTION",
};
export const supportTicketTypesToIdMapper: any = {
  SUGGEST_A_FEATURE: jiraIdMapper["Feature Request"],
  REPORT_A_BUG: jiraIdMapper["Report a Bug"],
  ASK_QUESTION: jiraIdMapper["Question"],
};
export const priorityDataSource = (t?: any) => {
  if (!t) t = (s: string) => s;
  return [
    { id: "1", label: t("Highest") },
    { id: "2", label: t("High") },
    { id: "3", label: t("Medium") },
    { id: "4", label: t("Low") },
    { id: "5", label: t("Lowest") },
  ];
};

export const impactDataSource = (t?: any) => {
  if (!t) t = (s: string) => s;
  return [
    {
      id: "10000",
      label: t("Extensive"),
      description: t(
        "The entire system is down or inaccessible, preventing everyone from working.",
      ),
    },
    {
      id: "10001",
      label: t("Significant"),
      description: t(
        "You cannot complete a critical part of your job, and there is no workaround available.",
      ),
    },
    {
      id: "10002",
      label: t("Moderate"),
      description: t(
        "A key feature is broken or not functioning properly, but you can continue your work using a workaround.",
      ),
    },
    {
      id: "10003",
      label: t("Minor"),
      description: t(
        "A typo, visual issue, or minor inconvenience that does not affect your work.",
      ),
    },
  ];
};
export const jiraStatusMapper = (t?: any): Record<string, string> => {
  if (!t) t = (s: string) => s;
  return {
    "10003": t("Waiting for support"), // To Do
    "10005": t("Pending"), // To Do
    "1": t("Open"), // To Do
    "10034": t("Under Consideration"), // To Do
    "10019": t("Under investigation"), // To Do
    "10038": t("Added to Roadmap"), // To Do
    "10035": t("Accepted"), // In Progress
    "3": t("In Progress"), // In Progress
    "10004": t("Waiting for customer"), // In Progress
    "10011": t("Developed"), // In Progress
    "5": t("Resolved"), // Done (green)
    "10036": t("Released"), // Done (green)
    "10057": t("Already Available"), // Done (green)
    "10006": t("Canceled"), // Done (red)
    "10002": t("Declined"), // Done (red)
    "6": t("Closed"), // Done (blue, terminal)
  };
};

export const jiraStatusLabelToId: Record<string, string> = Object.fromEntries(
  Object.entries(jiraStatusMapper()).map(([id, label]) => [label, id]),
);

export const jiraStatusCategoryMapper = (t?: any): Record<string, string> => {
  if (!t) t = (s: string) => s;
  return {
    "4": t("In Progress"),
    "2": t("To Do"),
    "3": t("Done"),
  };
};

export const severityDataSource = (t?: any) => {
  if (!t) t = (s: string) => s;
  return [
    { id: "1", label: t("Critical") },
    { id: "2", label: t("High") },
    { id: "3", label: t("Low") },
  ];
};

export const severityIdToLabel = (t?: any): Record<string, string> => {
  if (!t) t = (s: string) => s;
  return Object.fromEntries(severityDataSource(t).map((s) => [s.id, s.label]));
};

export const severityDataSourceCustomSla = (t?: any) => {
  if (!t) t = (s: string) => s;
  return [
    {
      id: "1",
      label: t("High"),
      description: t(
        "Production Environment is down or critical malfunction resulting in an inoperative condition or disrupts critical business functions and requires immediate attention.",
      ),
    },
    {
      id: "2",
      label: t("Medium"),
      description: t(
        "Loss of performance resulting in users being unable to perform their normal activities, as essential functions and critical programs are partially available. The environment is usable but functions not working properly.",
      ),
    },
    {
      id: "3",
      label: t("Low"),
      description: t(
        "Day to day operational issues like access of applications, network, etc.",
      ),
    },
  ];
};

export const severityIdToLabelCustomSla = (t?: any): Record<string, string> => {
  if (!t) t = (s: string) => s;
  return Object.fromEntries(
    severityDataSourceCustomSla(t).map((s) => [s.id, s.label]),
  );
};

export const priorityMatrix: Record<string, Record<string, string>> = {
  "10000": { "1": "1", "2": "1", "3": "2" }, // Extensive
  "10001": { "1": "1", "2": "2", "3": "3" }, // Significant
  "10002": { "1": "2", "2": "3", "3": "4" }, // Moderate
  "10003": { "1": "3", "2": "4", "3": "4" }, // Minor
};

const priorityLabels: Record<string, string> = {
  "1": "P1",
  "2": "P2",
  "3": "P3",
  "4": "P4",
};

export const getPriorityFromImpactAndSeverity = (
  impactId: string,
  severityId: string,
): { id: string; label: string } | null => {
  const priorityId = priorityMatrix[impactId]?.[severityId];
  if (!priorityId) return null;
  return { id: priorityId, label: priorityLabels[priorityId] ?? "" };
};

export interface ADFMark {
  type: string;
  attrs?: Record<string, any>;
}

export interface ADFNode {
  type: string;
  text?: string;
  attrs?: Record<string, any>;
  marks?: ADFMark[];
  content?: ADFNode[];
}

export interface ADFDoc {
  type: "doc";
  content: ADFNode[];
}
export const convertAdfToHtml = (adf: ADFDoc | null | undefined): string => {
  if (!adf || adf.type !== "doc" || !adf.content) return "";

  const renderText = (node: ADFNode): string => {
    let text = node.text || "";
    if (node.marks) {
      node.marks.forEach((mark: ADFMark) => {
        switch (mark.type) {
          case "strong":
            text = `<strong>${text}</strong>`;
            break;
          case "em":
            text = `<em>${text}</em>`;
            break;
          case "underline":
            text = `<u>${text}</u>`;
            break;
          case "strike":
            text = `<s>${text}</s>`;
            break;
          case "code":
            text = `<code>${text}</code>`;
            break;
          case "link": {
            const href = mark.attrs?.href || "#";
            if (
              href.startsWith(
                `${process.env.REACT_APP_CUBE_BACKEND}/fileSystemServer/getAttachmentFile/`,
              )
            ) {
              text = `<div 
              class="relative inline-block w-fit h-fit"
              onmouseover="this.querySelector('.hover-icon').style.opacity = '1'"
              onmouseout="this.querySelector('.hover-icon').style.opacity = '0'"
              >
              <img
                src="${href}"
                alt="attachment"
                class="h-52 rounded shadow hover:!opacity-50"
              />
              <div
                id="commentImageOpenIcon"
                data-attachment-url="${href}"
                class="absolute cursor-pointer p-1 hover-icon"
                style="box-shadow: 0 2px 6px rgba(0,0,0,0.2); right:50%; top:50%; opacity: 0;"
              >
              <svg id="commentImageOpenIcon" xmlns="http://www.w3.org/2000/svg" 
              width="16" height="16" fill="none" 
              viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path id="commentImageOpenIcon" stroke-linecap="round" stroke-linejoin="round" 
                 d="M18 13v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h6m7-3h-4m4 0v4m0-4L10 14" />
              </svg>
              </div>
            </div>`;
            } else {
              text = `<a href="${href}" class="text-blue-600 underline" target="_blank">${text}</a>`;
            }
            break;
          }
          case "textColor": {
            const color = mark.attrs?.color || "#000000";
            text = `<span style="color: ${color}">${text}</span>`;
            break;
          }
          default:
            text = ""; // unrecognized mark
        }
      });
    }
    return text;
  };

  const renderNode = (node: ADFNode): string => {
    switch (node.type) {
      case "paragraph":
        return `<p>${renderContent(node.content)}</p>`;
      case "heading": {
        const level = node.attrs?.level || 1;
        return `<h${level}>${renderContent(node.content)}</h${level}>`;
      }
      case "bulletList":
        return `<ul>${renderContent(node.content)}</ul>`;
      case "orderedList":
        return `<ol>${renderContent(node.content)}</ol>`;
      case "listItem":
        return `<li>${renderContent(node.content)}</li>`;
      case "blockquote":
        return `<blockquote>${renderContent(node.content)}</blockquote>`;
      case "codeBlock":
        return `<pre><code>${renderContent(node.content)}</code></pre>`;
      case "panel":
        return `<div class="panel">${renderContent(node.content)}</div>`;
      case "rule":
        return `<hr />`;
      case "hardBreak":
        return "<br/>";
      case "text":
        return renderText(node);
      case "mediaSingle":
        return `<p class='opacity-30'>Media uploaded from JIRA are not visible here in comments section</p>`;
      case "media":
        return `<p class='opacity-30'>Media uploaded from JIRA are not visible here in comments section</p>`;
      case "table":
        return `<table>${renderContent(node.content)}</table>`;
      case "tableRow":
        return `<tr>${renderContent(node.content)}</tr>`;
      case "tableCell":
        return `<td>${renderContent(node.content)}</td>`;
      case "tableHeader":
        return `<th>${renderContent(node.content)}</th>`;
      default:
        return renderContent(node.content); // fallback for unknown node types
    }
  };

  const renderContent = (content: ADFNode[] = []): string => {
    return content.map(renderNode).join("");
  };

  return renderContent(adf.content);
};

// Maps each status id to its issueStatusCategory
export const statusCategoryMapper = (
  t?: any,
): Record<string, { id: string; label: string }> => {
  if (!t) t = (s: string) => s;
  return {
    "10003": { id: "2", label: t("To Do") }, // Waiting for support
    "10005": { id: "2", label: t("To Do") }, // Pending
    "1": { id: "2", label: t("To Do") }, // Open
    "10034": { id: "2", label: t("To Do") }, // Under Consideration
    "10019": { id: "2", label: t("To Do") }, // Under investigation
    "10038": { id: "2", label: t("To Do") }, // Added to Roadmap
    "10035": { id: "3", label: t("In Progress") }, // Accepted
    "3": { id: "3", label: t("In Progress") }, // In Progress
    "10004": { id: "3", label: t("In Progress") }, // Waiting for customer
    "10011": { id: "3", label: t("In Progress") }, // Developed
    "5": { id: "4", label: t("Done") }, // Resolved
    "10036": { id: "4", label: t("Done") }, // Released
    "10006": { id: "4", label: t("Done") }, // Canceled
    "6": { id: "4", label: t("Done") }, // Closed
    "10002": { id: "4", label: t("Done") }, // Declined
    "10057": { id: "4", label: t("Done") }, // Already Available
  };
};

// Per-status tailwind background color token for status badges.
// Falls back to category-based color when an unknown status id is passed.
export const jiraStatusColorTokens = {
  toDo: "bg-system_colour_45", // slate grey
  inProgress: "bg-system_colour_12", // amber/yellow
  doneGreen: "bg-positive_communication", // resolved / released / already available
  doneRed: "bg-negative_communication", // canceled / declined
  doneBlue: "bg-system_colour_1", // closed (terminal)
} as const;

const statusIdToColorToken: Record<string, string> = {
  "10006": jiraStatusColorTokens.doneRed, // Canceled
  "10002": jiraStatusColorTokens.doneRed, // Declined
  "6": jiraStatusColorTokens.doneBlue, // Closed
  "5": jiraStatusColorTokens.doneGreen, // Resolved
  "10036": jiraStatusColorTokens.doneGreen, // Released
  "10057": jiraStatusColorTokens.doneGreen, // Already Available
};

export const getJiraStatusColor = (
  statusId?: string,
  statusCategoryId?: string,
): string => {
  if (statusId && statusIdToColorToken[statusId])
    return statusIdToColorToken[statusId];
  if (statusCategoryId === "2") return jiraStatusColorTokens.toDo;
  if (statusCategoryId === "3") return jiraStatusColorTokens.inProgress;
  if (statusCategoryId === "4") return jiraStatusColorTokens.doneGreen;
  return "";
};

export const jiraStatusColorHex = {
  toDo: "#666a84", // system_colour_45 (slate grey)
  inProgress: "#ffcf55", // system_colour_12 (amber/yellow)
  doneGreen: "#45cf80", // positive_communication
  doneRed: "#e75555", // negative_communication
  doneBlue: "#1080d6", // system_colour_1
} as const;

const statusIdToColorHex: Record<string, string> = {
  // To Do (slate grey)
  "1": jiraStatusColorHex.toDo, // Open
  "10003": jiraStatusColorHex.toDo, // Waiting for support
  "10005": jiraStatusColorHex.toDo, // Pending
  "10034": jiraStatusColorHex.toDo, // Under Consideration
  "10019": jiraStatusColorHex.toDo, // Under investigation
  "10038": jiraStatusColorHex.toDo, // Added to Roadmap
  // In Progress (amber/yellow)
  "10035": jiraStatusColorHex.inProgress, // Accepted
  "3": jiraStatusColorHex.inProgress, // In Progress
  "10004": jiraStatusColorHex.inProgress, // Waiting for customer
  "10011": jiraStatusColorHex.inProgress, // Developed
  // Done — green
  "5": jiraStatusColorHex.doneGreen, // Resolved
  "10036": jiraStatusColorHex.doneGreen, // Released
  "10057": jiraStatusColorHex.doneGreen, // Already Available
  // Done — red
  "10006": jiraStatusColorHex.doneRed, // Canceled
  "10002": jiraStatusColorHex.doneRed, // Declined
  // Done — blue (terminal)
  "6": jiraStatusColorHex.doneBlue, // Closed
};

export const getJiraStatusHex = (
  statusId?: string,
  statusCategoryId?: string,
): string => {
  if (statusId && statusIdToColorHex[statusId])
    return statusIdToColorHex[statusId];
  if (statusCategoryId === "2") return jiraStatusColorHex.toDo;
  if (statusCategoryId === "3") return jiraStatusColorHex.inProgress;
  if (statusCategoryId === "4") return jiraStatusColorHex.doneGreen;
  return "";
};

// Maps: issueTypeId → currentStatusId → array of next status IDs
export const issueTransitions: Record<string, Record<string, string[]>> = {
  // Report a Bug — global: any non-terminal → Open ("1")
  "10013": {
    "1": ["10019", "10004"],
    "10019": ["10035", "1", "10006", "10004"],
    "10035": ["3", "1", "10004"],
    "3": ["10011", "10006", "1", "10004"],
    "10011": ["3", "10036", "1", "10004"],
    "10036": ["10006", "6", "1", "10004"],
    "10006": ["6", "1", "10004"],
    "10004": ["1", "10019", "10035", "3", "10011", "10036", "10006", "6"],
    "6": [],
  },
  // Ask a Question
  "10010": {
    "10003": ["3", "10005", "10006", "10004"],
    "10005": ["3", "10004"],
    "3": ["10004", "5", "10006"],
    "10004": ["3", "5", "6"],
    "5": ["6"],
    "10006": ["6"],
    "6": [],
  },
  // Feature Request — global: any non-terminal → Pending ("10005");
  // Waiting for customer ("10004") is any-to-any, same as Report a Bug
  "10012": {
    "10005": ["10034", "10004"],
    "10034": ["10035", "10057", "10002", "10005", "10004"],
    "10035": ["3", "10038", "10005", "10004"],
    "3":     ["10011", "10005", "10004"],
    "10011": ["10036", "10005", "10004"],
    "10038": ["10036", "10005", "10004"],
    "10036": ["6", "10005", "10004"],
    "10002": ["6", "10005", "10004"],
    "10057": ["10005", "10004"],
    "10004": ["10005", "10034", "10035", "3", "10011", "10038", "10036", "10002", "10057", "6"],
    "6":     [],
  },
};

export const initialStatusIdByIssueType: Record<string, string> = {
  "10010": "10003", // Ask a Question → Waiting for support
  "10012": "10005", // Feature Request → Pending
  "10013": "1", // Report a Bug → Open
};

export function buildTransition(
  statusId: string,
  t?: any,
): {
  id: string;
  label: string;
  issueStatusCategory: { id: string; label: string };
} {
  return {
    id: statusId,
    label: jiraStatusMapper(t)[statusId]!,
    issueStatusCategory: statusCategoryMapper(t)[statusId]!,
  };
}

export const getStatusIdsForIssueType = (issueTypeId: string): string[] => {
  const map = issueTransitions[issueTypeId];
  if (!map) return [];
  const ids = new Set<string>(Object.keys(map));
  for (const next of Object.values(map)) {
    for (const id of next) ids.add(id);
  }
  return Array.from(ids);
};

export const getStatusesForIssueType = (
  issueTypeId: string,
  t?: any,
): Array<{ id: string; label: string }> => {
  const statusMap = jiraStatusMapper(t);
  return getStatusIdsForIssueType(issueTypeId).map((id) => ({
    id,
    label: statusMap[id] ?? id,
  }));
};

// `_id` is the DEFAULT_SYSTEM_USER sentinel (helpers/constants.ts) — kept a literal so it stays
// assignable to the `ObjectId | "0"` author refs on `jiraTickets.activity[]`.
export const CUBE_SUPPORT_AUTHOR = {
  _id: "0" as const,
  fullName: "Cube Support",
};
